import { FC } from "react"
import Image from "next/image"
import classNames from "classnames"
import { readImage } from "../../app/utils/read-image"
import { BlokImage } from "../../storyblok"
import { safelyRenderHtml } from "@/util/safelyRenderHtml"
import { useGeoDataEffect } from "../../hooks/useGeoData"
import { PREMIUM_HERO_FORM_ATF_LAYOUT, PremiumHeroForm } from "./hero-form"
import useScreenSize from "@/hooks/useScreenSize"

interface FeaturedLogo {
  _uid: string
  image: BlokImage
  width: string
  height: string
  component: string
}

export interface IPetsHeroATFFeatured {
  blok: {
    title: string
    description: string
    buttonLink?: string
    buttonLabel?: string
    image?: BlokImage
    bgSmImage?: BlokImage
    geoPostDescription?: string
    geoTitle?: string
    hasGeoCustomization?: boolean
    hasSwapCustomization?: boolean
    hasListingForm?: boolean
    animalType?: string
    bgImagePlacement?: string
    // Featured On props
    featuredHeader?: string
    featuredHeaderDesktop?: string
    featuredLogos?: FeaturedLogo[]
  }
}

// Mobile: Simplified inline Featured In section
const MobileFeaturedIn: FC<{
  header: string
  logos: FeaturedLogo[]
}> = ({ header, logos }) => {
  if (!logos || logos.length === 0) return null

  return (
    <div className="flex flex-row items-center justify-start w-full gap-3 sm:gap-4 pt-2 pb-6 sm:justify-center sm:py-6 overflow-hidden">
      <span className="text-gray-700 text-xs sm:text-sm font-inter font-semibold whitespace-nowrap shrink-0">
        {header}
      </span>
      <div className="flex items-center gap-3 sm:gap-4">
        {logos.map((logo) => (
          <Image
            key={logo._uid}
            src={readImage(logo.image)}
            alt={logo.image.alt || "featured logo"}
            height={Number(logo.height) || 20}
            width={Number(logo.width) || 80}
            className="h-5 w-auto object-contain max-w-[30%] sm:max-w-none sm:h-5"
            priority
          />
        ))}
      </div>
    </div>
  )
}

// Desktop: Full Featured In section (same as PremiumAsFeaturedIn)
const DesktopFeaturedIn: FC<{
  header: string
  logos: FeaturedLogo[]
}> = ({ header, logos }) => {
  if (!logos || logos.length === 0) return null

  return (
    <section
      id="as-featured-in"
      className="max-w-[1400px] w-full mx-auto my-16 md:mt-24 md:mb-20 md:pb-10"
    >
      <h2 className="mb-10 md:mb-16 font-montserrat font-normal text-center uppercase">
        <div className="flex items-center justify-center md:pt-10">
          <div className="border-t border-gold w-16 mr-2"></div>
          <span className="text-gold text-2xl uppercase font-inter">
            {header}
          </span>
          <div className="border-t border-gold w-16 ml-2"></div>
        </div>
      </h2>
      <div className="flex items-center justify-center mx-auto px-8 md:px-3 gap-y-10 gap-x-12 sm:gap-2 lg:gap-10 flex-wrap sm:flex-no-wrap lg:flex-wrap xl:flex-nowrap">
        {logos.map((logo) => (
          <Image
            key={logo._uid}
            src={readImage(logo.image)}
            alt={logo.image.alt || "featured logo"}
            height={Number(logo.height) || 38}
            width={Number(logo.width) || 172}
          />
        ))}
      </div>
    </section>
  )
}

const PetsHeroATFFeatured: FC<IPetsHeroATFFeatured> = (props) => {
  const {
    title,
    description,
    buttonLabel,
    buttonLink,
    bgSmImage,
    image,
    hasGeoCustomization = false,
    hasSwapCustomization = false,
    geoPostDescription = "Hire top-rated transporters in {location}.",
    geoTitle = "",
    hasListingForm = false,
    // Featured On props
    featuredHeader = "Featured On",
    featuredHeaderDesktop = "Rated #1 in Pet Shipping",
    featuredLogos = [],
  } = props.blok

  const showOnDesktop = useScreenSize({ min: "md" })
  const { geoDescription, geoSessionValue } = useGeoDataEffect(
    geoPostDescription,
  )
  const geoLocationTitle = useGeoDataEffect(geoTitle)
  const useMobileImage = bgSmImage?.filename ? bgSmImage : image

  return (
    <>
      <section
        id="hero-section"
        className="relative md:flex md:h-[645px] bg-white md:bg-secondary-100 mx-auto justify-center md:pt-8"
      >
        {/* Hero text content */}
        <div className="w-full md:w-1/2 lg:w-2/3 lg:max-w-[651px]">
          <div
            className={`h-auto md:h-full relative max-w-4xl lg:max-w-5xl xl:max-w-7xl mx-auto px-6 lg:px-8 pt-1 md:pt-6 pb-2 md:py-8`}
          >
            <div className="text-black font-inter max-w-3xl text-left md:mt-10 lg:mt-20 mb-0">
              <h1 className="text-[32px] leading-[46px] md:leading-[70px] sm:text-[42px] md:text-[56px] font-bold lg:text-[56px] mt-1.5 mb-2 md:mt-4 md:mb-6">
                {geoLocationTitle.geoDescription && geoLocationTitle.isUSVisitor
                  ? geoLocationTitle.geoDescription
                  : title}
              </h1>
              <div
                id="PremiumHero-description"
                className="text-[17px] leading-9 md:leading-10 max-w-lg font-inter mb-0"
              >
                {safelyRenderHtml(
                  `${description} ${
                    hasGeoCustomization ? geoDescription : ""
                  } `,
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Desktop hero image - shown at md+ (768px) */}
        <div
          className={classNames(
            "w-full md:w-1/2 lg:w-[500px] md:flex md:items-start",
            !showOnDesktop && "hidden",
          )}
        >
          {image?.filename && (
            <Image
              src={readImage(image)}
              alt={image.alt || "hero image"}
              width={492}
              height={428}
              className={classNames(
                "w-full max-w-[492px] h-auto object-cover object-center",
                bgSmImage?.filename ? "hidden md:block" : "block",
              )}
            />
          )}
        </div>

        {/* Form container - absolutely positioned at md+ */}
        <div
          className={classNames(
            "relative md:absolute md:-bottom-24 max-w-7xl mx-auto md:flex justify-center bg-white rounded-1 px-6 md:px-5 lg:px-8 py-6 md:py-11 right-0 left-0",
            hasListingForm ? "md:h-[300px]" : "md:h-[240px]",
          )}
        >
          <div data-testid="form-atf" className="w-full md:w-auto">
            {/* MOBILE ONLY: Featured In (simplified) - shows BEFORE form on mobile */}
            <div className="md:hidden">
              <MobileFeaturedIn header={featuredHeader} logos={featuredLogos} />
            </div>

            <PremiumHeroForm
              buttonLabel={buttonLabel}
              buttonLink={buttonLink}
              hasSwapCustomization={hasSwapCustomization}
              geoSessionValue={geoSessionValue as string}
              hideLabel
              font="font-inter"
              customClassName={PREMIUM_HERO_FORM_ATF_LAYOUT}
            />
          </div>
        </div>
      </section>

      {/* MOBILE/TABLET: Pet image - shows AFTER form until md breakpoint */}
      <div className="px-6 mt-4 md:hidden">
        <div className="w-full min-h-[328px] mx-auto flex justify-center">
          {useMobileImage?.filename && (
            <Image
              className="object-contain"
              src={readImage(useMobileImage)}
              alt={useMobileImage?.alt ?? "pets"}
              width={320}
              height={400}
            />
          )}
        </div>
      </div>

      {/* DESKTOP ONLY: Featured In (full gold-lined style) - shows AFTER form */}
      <div className="hidden md:block">
        <DesktopFeaturedIn
          header={featuredHeaderDesktop}
          logos={featuredLogos}
        />
      </div>

      <section className="md:mb-10" />
    </>
  )
}

export default PetsHeroATFFeatured
